import { useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import Plot from 'react-plotly.js'
import { TOKENS, FONT_SANS, FONT_MONO } from './OptimiserDesign'

const PALETTE = ['#4472C4', '#70AD47', '#ED7D31', '#FFC000', '#9E4DD6', '#5DADE2', '#E74C3C', '#26C6DA']
const formatPeriod = (p: number) => `W${String(p).padStart(2, '0')}`
const PLOTLY_CONFIG = { displayModeBar: false, responsive: true }

type Row = Record<string, unknown>
const getStr = (r: Row, k: string) => String(r[k] ?? '')
const getNum = (r: Row, k: string) => Number(r[k] ?? 0)

const PillSelector = ({
  label, items, active, onSelect, getLabel,
}: {
  label: string
  items: { key: string }[]
  active: string | null
  onSelect: (key: string) => void
  getLabel: (item: { key: string }) => string
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
    <Typography sx={{ fontFamily: FONT_SANS, fontSize: 10, fontWeight: 700, color: TOKENS.ink400,
                      textTransform: 'uppercase', letterSpacing: '0.05em', mr: 0.25 }}>
      {label}
    </Typography>
    {items.map((item) => (
      <Box
        key={item.key}
        onClick={() => onSelect(item.key)}
        sx={{
          px: '8px', py: '2px', borderRadius: '100px', fontSize: 10.5,
          fontFamily: FONT_MONO, fontWeight: 600, cursor: 'pointer',
          border: `1.5px solid ${active === item.key ? TOKENS.brandTeal : TOKENS.line300}`,
          color: active === item.key ? TOKENS.brandTeal : TOKENS.ink700,
          background: active === item.key ? TOKENS.blue50 : 'transparent',
          '&:hover': { borderColor: TOKENS.brandTeal },
        }}
      >
        {getLabel(item)}
      </Box>
    ))}
  </Box>
)

export const ProductionLineChart = ({
  prodRows,
  capRows,
}: {
  prodRows: Row[]
  capRows: Row[]
}) => {
  const [selectedLine, setSelectedLine] = useState<string | null>(null)
  const hasCapacity = capRows.length > 0

  // Reset when data changes (run switch)
  useEffect(() => { setSelectedLine(null) }, [prodRows, capRows])

  const lines = useMemo(() => {
    if (hasCapacity) {
      const seen = new Set<string>()
      capRows.forEach((r) => seen.add(`${getStr(r, 'resource')}:::${getStr(r, 'supplier')}`))
      return Array.from(seen).sort().map((k) => {
        const [resource, supplier] = k.split(':::')
        return { key: k, resource: resource ?? '', supplier: supplier ?? '' }
      })
    }
    const seen = new Set<string>()
    prodRows.forEach((r) => seen.add(getStr(r, 'supplier')))
    return Array.from(seen).sort().map((s) => ({ key: `:::${s}`, resource: '', supplier: s }))
  }, [capRows, prodRows, hasCapacity])

  const activeLine = selectedLine ?? lines[0]?.key ?? null
  const [activeResource, activeSupplier] = (activeLine ?? ':::').split(':::')

  const filteredProd = useMemo(
    () => (activeSupplier ? prodRows.filter((r) => getStr(r, 'supplier') === activeSupplier) : prodRows),
    [prodRows, activeSupplier]
  )

  const allPeriods = useMemo(() => {
    const ps = new Set<number>()
    filteredProd.forEach((r) => ps.add(getNum(r, 'period')))
    return Array.from(ps).sort((a, b) => a - b)
  }, [filteredProd])

  const products = useMemo(() => {
    const ps = new Set<string>()
    filteredProd.forEach((r) => ps.add(getStr(r, 'product')))
    return Array.from(ps).sort()
  }, [filteredProd])

  const qtyMap = useMemo(() => {
    const m: Record<string, Record<number, number>> = {}
    filteredProd.forEach((r) => {
      const p = getStr(r, 'product')
      const t = getNum(r, 'period')
      m[p] = m[p] ?? {}
      m[p][t] = (m[p][t] ?? 0) + getNum(r, 'value')
    })
    return m
  }, [filteredProd])

  const periodLabels = allPeriods.map(formatPeriod)

  const totalByPeriod = allPeriods.map((t) =>
    products.reduce((sum, p) => sum + (qtyMap[p]?.[t] ?? 0), 0)
  )

  const productTraces = products.map((p, i) => ({
    type: 'bar' as const,
    name: p,
    x: periodLabels,
    y: allPeriods.map((t) => qtyMap[p]?.[t] ?? 0),
    marker: { color: PALETTE[i % PALETTE.length] },
  }))

  const totalTrace = {
    type: 'scatter' as const, mode: 'lines' as const, name: 'Total',
    x: periodLabels, y: totalByPeriod,
    line: { color: TOKENS.ink700, dash: 'dash' as const, width: 1.5 },
  }

  // Capacity overlay (Phase 3+)
  const filteredCap = useMemo(
    () => capRows.filter(
      (r) => getStr(r, 'resource') === activeResource && getStr(r, 'supplier') === activeSupplier
    ),
    [capRows, activeResource, activeSupplier]
  )
  const capPeriods = useMemo(() => {
    const ps = new Set<number>()
    filteredCap.forEach((r) => ps.add(getNum(r, 'period')))
    return Array.from(ps).sort((a, b) => a - b)
  }, [filteredCap])
  const capLabels = capPeriods.map(formatPeriod)
  const loadArr = capPeriods.map((t) => {
    const r = filteredCap.find((row) => getNum(row, 'period') === t)
    return r ? getNum(r, 'load') : 0
  })
  const capArr = capPeriods.map((t) => {
    const r = filteredCap.find((row) => getNum(row, 'period') === t)
    return r ? getNum(r, 'cap') : 0
  })
  const muArr = capPeriods.map((t) => {
    const r = filteredCap.find((row) => getNum(row, 'period') === t)
    return r ? getNum(r, 'mu_target') : 0
  })

  if (prodRows.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: 12, color: TOKENS.ink300 }}>
          No production data
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${TOKENS.line100}`, flexShrink: 0,
                 display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: 11.5, fontWeight: 700, color: TOKENS.ink900 }}>
          Production Line Plan
        </Typography>
        {lines.length > 0 && (
          <PillSelector
            label={hasCapacity ? 'LINE' : 'SUPPLIER'}
            items={lines}
            active={activeLine}
            onSelect={setSelectedLine}
            getLabel={(item) => {
              const l = item as { key: string; resource: string; supplier: string }
              return l.resource ? `${l.resource} · ${l.supplier}` : l.supplier
            }}
          />
        )}
      </Box>

      {/* Volume chart — fills remaining space */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Plot
          data={[...productTraces, totalTrace]}
          layout={{
            paper_bgcolor: 'transparent', plot_bgcolor: TOKENS.surface50,
            font: { family: FONT_SANS, size: 10, color: TOKENS.ink700 },
            margin: { l: 44, r: 8, t: 8, b: 44 }, autosize: true, barmode: 'stack',
            legend: { orientation: 'h', y: -0.22, x: 0, font: { size: 9 } },
            xaxis: { tickfont: { size: 9, family: FONT_MONO }, gridcolor: TOKENS.line200 },
            yaxis: { tickfont: { size: 9, family: FONT_MONO }, gridcolor: TOKENS.line200,
                     title: { text: 'Qty', font: { size: 9 } } },
          }}
          config={PLOTLY_CONFIG}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
        />
      </Box>

      {/* Capacity chart — only when Phase 3+ */}
      {hasCapacity && filteredCap.length > 0 && (
        <Box sx={{ flex: 1, minHeight: 0, borderTop: `1px solid ${TOKENS.line100}` }}>
          <Plot
            data={[
              { type: 'bar', name: 'Load', x: capLabels, y: loadArr, marker: { color: '#4472C4' } },
              { type: 'scatter', mode: 'lines', name: 'Capacity', x: capLabels, y: capArr,
                line: { color: '#C00000', width: 2 } },
              { type: 'scatter', mode: 'lines', name: 'Min-util', x: capLabels, y: muArr,
                line: { color: '#C00000', dash: 'dot', width: 1.5 } },
            ]}
            layout={{
              paper_bgcolor: 'transparent', plot_bgcolor: TOKENS.surface50,
              font: { family: FONT_SANS, size: 10, color: TOKENS.ink700 },
              margin: { l: 44, r: 8, t: 8, b: 44 }, autosize: true, barmode: 'group',
              legend: { orientation: 'h', y: -0.22, x: 0, font: { size: 9 } },
              xaxis: { tickfont: { size: 9, family: FONT_MONO }, gridcolor: TOKENS.line200 },
              yaxis: { tickfont: { size: 9, family: FONT_MONO }, gridcolor: TOKENS.line200,
                       title: { text: 'Hours', font: { size: 9 } } },
            }}
            config={PLOTLY_CONFIG}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
          />
        </Box>
      )}
    </Box>
  )
}
